import React, { useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { FiLink, FiDownload } from 'react-icons/fi'
import { IoShareSocialOutline } from 'react-icons/io5'

import { LIST_TIME } from '_utils/constant'
import { convertToCurrentGMT, getPreciseTimeLeft } from '_utils/function'
import ModalExternalLink from '../../../components/ModalExternalLink'
import MenuShareTemplate from '../../../components/MenuShareTemplate'
import './style.scss'
import useEventCardActions from '../../../hooks/useEventCardActions'
import useShareTemplate from '../../../hooks/useShareTemplate'

const getCollectionTimeRange = (event) => {
  if (!event?.pickupTime && !event?.pickupTimestamp) return ''
  try {
    const timeStr = event.pickupTime
      ? convertToCurrentGMT(event.pickupTime)
      : moment.unix(event.pickupTimestamp).format('DD-MM-YYYY HH:mm')
    const hourStr = timeStr?.split(' ')?.[1]?.split(':')?.[0]
    const timeStart = Number(hourStr)
    const duration = event.pickupDuration || event.ePickupDuration
    if (!isNaN(timeStart) && duration) {
      const timeEnd = timeStart + Number(duration) / 3600
      const startItem = LIST_TIME.find((item) => item.id === timeStart)
      const endItem = LIST_TIME.find((item) => item.id === (timeEnd % 24))
      if (startItem && endItem) {
        return ` (${startItem.text} - ${endItem.text})`
      }
    }
  } catch (e) {
    // ignore
  }
  return ''
}

const EventCard = ({ event, variant, isClosed }) => {
  const {
    useAsTemplate,
    editEvent,
    goToManageOrders,
    shareEvent,
    isSharing,
  } = useEventCardActions()

  const {
    isShareTemplateMenuOpen,
    onClickIconDownload,
    closeMenu,
    handleDownloadExcel,
    handleDownloadPDF,
    handleUseAsTemplate,
    toggleShareTemplate,
    isUpdatingShare,
  } = useShareTemplate()

  const closingLeft = getPreciseTimeLeft(event.closingTimestamp)
  const isClosingOver = closingLeft.days <= 0
  const isClosedState = isClosed || isClosingOver

  const extraFieldsList = Array.isArray(event?.extraFields)
    ? event.extraFields
    : (event?.extraFields?.list || [])
  const linksCount = extraFieldsList.length
  const badgeCount = event.totalOrderCount || 0

  const closingDateStr = event.closingTime
    ? moment(convertToCurrentGMT(event.closingTime), 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY')
    : (event.closingTimestamp ? moment.unix(event.closingTimestamp).format('DD-MM-YYYY') : '—')

  const collectionDateBase = event.pickupTime
    ? moment(convertToCurrentGMT(event.pickupTime), 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY')
    : (event.pickupTimestamp ? moment.unix(event.pickupTimestamp).format('DD-MM-YYYY') : '—')

  const collectionRange = getCollectionTimeRange(event)
  const collectionDateStr =
    collectionDateBase !== '—' ? `${collectionDateBase}${collectionRange}` : '—'

  const addressStr = event.pickupAddress || '—'

  const [isExternalLinkModalOpen, setIsExternalLinkModalOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  const stopPropagation = (fn) => (e) => {
    e.stopPropagation()
    fn()
  }

  const onClickShare = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isSharing || isCopied) return
    shareEvent(event?.id, (url) => {
      navigator.clipboard.writeText(url)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    })
  }

  return (
    <div
      className={`hosted-event-card event-card hosted-event-card--${variant}${
        isClosedState ? ' is-closed' : ''
      }`}
      data-title={event.title}
      onClick={() => goToManageOrders(event.id)}
    >
      <div className="hosted-event-card__image et-img">
        {event.photoUrls?.[0] ? (
          <img src={event.photoUrls[0]} alt={event.title} />
        ) : (
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isClosedState ? '#888780' : 'var(--teal, #009A97)'}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ opacity: 0.5 }}
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        )}
        <span
          className={`hosted-event-card__badge et-buy-badge ${
            isClosedState ? 'closed' : badgeCount > 0 ? 'hot' : 'is-zero'
          }`}
        >
          {badgeCount} BUY
        </span>
      </div>

      <div className="hosted-event-card__body et-body">
        <div
          className={`hosted-event-card__status et-closing${
            isClosedState ? ' is-closed' : ''
          }`}
        >
          {isClosedState ? 'Closed' : `Closing in ${closingLeft.label}`}
        </div>
        <div className="hosted-event-card__title et-title" title={event.title}>
          {event.title}
        </div>
        <div className="hosted-event-card__desc et-desc">{event.description || ''}</div>
        <div className="et-info-grid">
          <span className="et-info-lbl">Closing:</span>
          <span className="et-info-val">{closingDateStr}</span>
          <span className="et-info-lbl">Collection:</span>
          <span className="et-info-val">{collectionDateStr}</span>
          <span className="et-info-lbl">Address:</span>
          <span className="et-info-val">{addressStr}</span>
        </div>
      </div>

      <div className="et-link-row" onClick={(e) => e.stopPropagation()}>
        {linksCount > 0 && (
          <div className="mef-external-link">
            {linksCount === 1 && extraFieldsList[0] && (
              <a
                href={
                  extraFieldsList[0]?.value
                    ? extraFieldsList[0].value.startsWith('http')
                      ? extraFieldsList[0].value
                      : `https://${extraFieldsList[0].value}`
                    : '#'
                }
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mef-external-link__anchor"
              >
                <FiLink size={10} />
                <span>{extraFieldsList[0]?.name || extraFieldsList[0]?.value}</span>
              </a>
            )}
            {linksCount > 1 && (
              <span
                className="mef-external-link__multi"
                role="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsExternalLinkModalOpen(true)
                }}
              >
                <FiLink size={10} />
                <span>{linksCount} Links</span>
              </span>
            )}
          </div>
        )}

        <div className="mef-row-actions">
          {event?.isSharingTemplate && (
            <div className="mef-share-template-wrapper">
              <FiDownload
                size={15}
                className="mef-share-template"
                onClick={onClickIconDownload}
              />
              {isShareTemplateMenuOpen && (
                <MenuShareTemplate
                  isOpen={isShareTemplateMenuOpen}
                  onClose={closeMenu}
                  onDownloadExcel={() => handleDownloadExcel(event.id)}
                  onDownloadPDF={() => handleDownloadPDF(event.id)}
                  onUseAsTemplate={() => handleUseAsTemplate(event.id)}
                />
              )}
            </div>
          )}
          <button
            type="button"
            className="mef-share-btn"
            aria-label="Share event"
            onClick={onClickShare}
            disabled={isSharing}
          >
            {isCopied ? (
              <span className="mef-share-btn__copied">Copied!</span>
            ) : (
              <IoShareSocialOutline size={15} />
            )}
          </button>
        </div>
      </div>

      <hr className="card-divider" />
      <div
        className="hosted-event-card__actions card-actions"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn secondary"
          onClick={stopPropagation(() => editEvent(event.id))}
        >
          Edit
        </button>
        <button
          type="button"
          className="btn secondary"
          onClick={stopPropagation(() => useAsTemplate(event.id))}
        >
          Rehost
        </button>
        <button
          type="button"
          className="btn secondary"
          disabled={isUpdatingShare}
          onClick={stopPropagation(() => toggleShareTemplate(event))}
        >
          {isUpdatingShare ? 'Updating…' : (event?.isSharingTemplate ? 'Stop Share' : 'Share Template')}
        </button>
      </div>
      {linksCount > 1 && (
        <ModalExternalLink
          isOpen={isExternalLinkModalOpen}
          toggle={() => setIsExternalLinkModalOpen(!isExternalLinkModalOpen)}
          extraFields={
            Array.isArray(event?.extraFields)
              ? { list: event.extraFields, enableSafeLinks: false }
              : event?.extraFields
          }
        />
      )}
    </div>
  )
}

EventCard.propTypes = {
  event: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['tile', 'list']).isRequired,
  isClosed: PropTypes.bool,
}

export default EventCard
